import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { branches } from '../../data/branches';
import { doctors } from '../../data/doctors';
import { MapPin, Phone, MessageCircle, Clock, Calendar, CheckCircle2, ArrowLeft } from 'lucide-react';
import MapSection from '../MapSection';
import DoctorCard from '../DoctorCard';
import PageHero from '../PageHero';
import { useLanguage } from '../../context/LanguageContext';

const BranchDetail = () => {
  const { slug } = useParams();
  const { t, getBranch } = useLanguage();
  const rawBranch = branches.find(b => b.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!rawBranch) {
    return <Navigate to="/branches" replace />;
  }

  const branch = getBranch(rawBranch);
  const branchDoctors = doctors.filter(d => rawBranch.doctors.includes(d.id));

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* Attractive Hero Banner matching Branches.js */}
      <PageHero
        badge={branch.city || branch.area || t('branchesSection.branchInfo')}
        title={branch.name}
        subtitle={branch.address}
      >
        <div className="mt-6 flex items-center justify-center">
          <Link 
            to="/branches"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-teal hover:text-white transition-colors border border-teal/40 hover:border-white px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-xs"
          >
            <ArrowLeft size={16} />
            <span>{t('branchesSection.viewAll')}</span>
          </Link>
        </div>
      </PageHero>

      {/* Main Content overlapping hero */}
      <div className="container mx-auto px-4 lg:px-8 -mt-16 relative z-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-10">
            {/* Branch Image Showcase */}
            {branch.image && (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                <div className="h-64 sm:h-80 md:h-96 w-full relative">
                  <img 
                    src={branch.image} 
                    alt={branch.name} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-6 right-6 text-white">
                    <span className="text-xs font-bold uppercase tracking-wider bg-clinical px-3 py-1 rounded-full">
                      {branch.city || branch.area}
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold mt-2">{branch.name}</h3>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 hover:border-gray-300 transition-colors">
              <h2 className="text-2xl font-bold text-navy mb-6">{t('branchesSection.branchInfo')}</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4 text-gray-700">
                  <MapPin className="text-clinical shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">{t('branchesSection.address')}</h3>
                    <p className="leading-relaxed">{branch.address}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 text-gray-700">
                  <Clock className="text-clinical shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">{t('branchesSection.timings')}</h3>
                    <p>{branch.timings.split('|')[0]}</p>
                    {branch.timings.split('|')[1] && <p>{branch.timings.split('|')[1]}</p>}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mt-8 pt-8 border-t border-gray-100">
                {branch.phones && branch.phones.length > 0 ? (
                  branch.phones.map((p, idx) => (
                    <a 
                      key={idx}
                      href={`tel:${p.replace(/\s+/g, '')}`}
                      className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-navy px-5 py-3 rounded-xl font-medium transition-colors"
                    >
                      <Phone size={18} />
                      <span>{t('branchesSection.callBranch')} {p}</span>
                    </a>
                  ))
                ) : (
                  <a 
                    href={`tel:${branch.phone.replace(/\s+/g, '')}`}
                    className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-navy px-6 py-3 rounded-xl font-medium transition-colors"
                  >
                    <Phone size={20} />
                    <span>{t('branchesSection.callBranch')} {branch.phone}</span>
                  </a>
                )}
                <a 
                  href={`https://wa.me/${branch.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  <MessageCircle size={20} />
                  <span>{t('branchesSection.whatsappMessage')}</span>
                </a>
              </div>
            </div>

            {/* Facilities */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 hover:border-gray-300 transition-colors">
              <h2 className="text-2xl font-bold text-navy mb-6">{t('branchesSection.availableFacilities')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {branch.facilities.map((facility, index) => (
                  <div key={index} className="flex items-center space-x-3 text-gray-700">
                    <CheckCircle2 className="text-teal" size={20} />
                    <span className="font-medium">{facility}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Doctors */}
            {branchDoctors.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-navy mb-6">{t('branchesSection.doctorsAtBranch')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {branchDoctors.map((doctor, index) => (
                    <DoctorCard key={doctor.id} doctor={doctor} index={index} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            <div className="bg-navy text-white p-8 rounded-2xl shadow-sm border border-navy/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-clinical/20 rounded-bl-full pointer-events-none"></div>
              <h3 className="text-2xl font-bold mb-4">{t('nav.bookAppointment')}</h3>
              <p className="text-gray-300 mb-6 text-sm leading-relaxed">
                {t('appointmentSection.formSubtitle')}
              </p>
              <Link 
                to="/appointment"
                state={{ branch: rawBranch.slug }}
                className="w-full flex items-center justify-center space-x-2 bg-clinical hover:bg-white hover:text-navy text-white px-6 py-4 rounded-xl font-bold transition-all shadow-md"
              >
                <Calendar size={20} />
                <span>{t('nav.bookAppointment')}</span>
              </Link>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
              <h3 className="font-bold text-navy mb-4">{t('branchesSection.findOnMap')}</h3>
              <div className="rounded-xl overflow-hidden border border-gray-200">
                <MapSection />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BranchDetail;
